import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PolishDateInput, { isValidDateValue } from '../common/PolishDateInput';
import { displayDateTime } from '../common/datePresentation';
import { getLessonTypesSetting } from '../common/lessonTypes';
import AuditService from '../../services/AuditService';
import TeacherService from '../../services/TeacherService';
import SettingsService from '../../services/SettingsService';
import SchoolClassService from '../../services/SchoolClassService';
import auditRecordDescription from './auditClassLabel';
import { changeFieldLabel, changeValueLabel } from './auditChangePresentation';
import { buildManageRouteWithSchoolYear, getNormalizedSchoolYearStartYearFromSearch } from './manageSchoolYearRouting';
import './AuditLogPage.scss';

const BASE_RECORD_TYPES = [
  ['accounts', 'Konta i uprawnienia'],
  ['classes', 'Grupy'], ['classstudents', 'Uczniowie w grupie'], ['lessonregister', 'Obecność i lekcje'],
  ['students', 'Uczniowie'], ['teachers', 'Nauczyciele'], ['clients', 'Klienci'], ['people', 'Osoby'],
  ['products', 'Produkty'], ['enrollments', 'Zapisy'], ['settings', 'Ustawienia'],
];
const BASE_ACTIONS = [
  ['created', 'Utworzenie'], ['updated', 'Zmiana'], ['deleted', 'Usunięcie'],
  ['member.added', 'Dodanie do grupy'], ['attendance.saved', 'Sprawdzenie obecności'],
  ['absence.reported', 'Zgłoszenie nieobecności'], ['absence.withdrawn', 'Cofnięcie nieobecności'],
  ['lesson.cancelled', 'Odwołanie zajęć'], ['lesson.restored', 'Przywrócenie zajęć'],
  ['permissions.updated', 'Zmiana uprawnień'],
];
const labelFor = (options, value) => (options.find(option => option[0] === value) || [value, value])[1];
const DETAIL_ROUTES = { clients: 'client', students: 'student', teachers: 'teacher', classes: 'class', products: 'product', enrollments: 'enrollment' };
const detailRoute = (entity, search) => {
  const segment = entity && DETAIL_ROUTES[entity.type];
  if (!segment || !entity.id) return null;
  const pathname = `/manage/${segment}/${encodeURIComponent(entity.id)}`;
  return buildManageRouteWithSchoolYear(pathname, '', getNormalizedSchoolYearStartYearFromSearch(search));
};
const localStart = value => (isValidDateValue(value) ? `${value}T00:00:00.000Z` : '');
const localEnd = value => (isValidDateValue(value) ? `${value}T23:59:59.999Z` : '');

function MultiFilter({ label, options, value, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const menu = useRef(null);
  const searchInput = useRef(null);
  const close = () => { setOpen(false); setSearch(''); };
  useEffect(() => {
    if (!open) return undefined;
    if (searchInput.current) searchInput.current.focus();
    const outside = event => {
      if (!trigger.current.contains(event.target) && !menu.current.contains(event.target)) close();
    };
    const escape = event => {
      if (event.key !== 'Escape') return;
      const focusWasInside = trigger.current.contains(document.activeElement) || menu.current.contains(document.activeElement);
      close();
      if (focusWasInside) trigger.current.focus();
    };
    document.addEventListener('click', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('click', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const shown = options.filter(option => option[1].toLocaleLowerCase('pl-PL').includes(search.toLocaleLowerCase('pl-PL')));
  return (
    <div className="audit-multifilter">
      <button
        ref={trigger}
        type="button"
        className="audit-multifilter-trigger"
        aria-expanded={open}
        aria-controls={`audit-filter-${label.replace(/\s+/g, '-')}`}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> {label}: {value.length ? `${value.length} wybrane` : 'Wszystkie'}
      </button>
      {open && (
        <div ref={menu} id={`audit-filter-${label.replace(/\s+/g, '-')}`} className="audit-multifilter-menu">
          <input ref={searchInput} className="form-control mb-2" aria-label={`Szukaj: ${label}`} placeholder="Szukaj…" value={search} onChange={event => setSearch(event.target.value)} />
          {shown.map(([id, name]) => (
            <label className="audit-multifilter-option" htmlFor={`audit-${label}-${id}`} key={id}>
              <input
                id={`audit-${label}-${id}`}
                type="checkbox"
                checked={value.includes(id)}
                onChange={() => onChange(value.includes(id) ? value.filter(item => item !== id) : [...value, id])}
              /> {name}
            </label>
          ))}
          {value.length > 0 && <button className="btn btn-link p-0" type="button" onClick={() => onChange([])}>Wyczyść wybór</button>}
        </div>
      )}
      {value.length > 0 && (
        <div className="audit-filter-chips">
          {value.map(id => (
            <button
              key={id}
              type="button"
              className="audit-filter-chip"
              aria-label={`Usuń ${labelFor(options, id)} z filtra ${label}`}
              onClick={() => onChange(value.filter(item => item !== id))}
            >
              {labelFor(options, id)} <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

MultiFilter.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

function AuditLogPage({ service = AuditService }) {
  const { t: translate } = useTranslation();
  const t = key => translate(`billing:teacherPayroll.${key}`);
  const RECORD_TYPES = [...BASE_RECORD_TYPES, ['teacherledger', t('audit.recordType')]];
  const ACTIONS = [...BASE_ACTIONS, ...['confirmed', 'approved', 'recalculated', 'corrected', 'issueReported', 'issueResolved']
    .map(action => [`teacherSettlement.${action}`, t(`audit.actions.${action}`)]), ['teacherRate.added', t('audit.actions.rateAdded')]];
  const history = useHistory();
  const location = useLocation();
  const relatedRecordId = new URLSearchParams(location.search).get('entityId') || '';
  const [types, setTypes] = useState([]);
  const [actions, setActions] = useState([]);
  const [actorIds, setActorIds] = useState([]);
  const [teacherIds, setTeacherIds] = useState([]);
  const [classTypeIds, setClassTypeIds] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [actorCatalog, setActorCatalog] = useState([]);
  const [actorInput, setActorInput] = useState('');
  const [entityId, setEntityId] = useState(relatedRecordId);
  const [text, setText] = useState('');
  const [searchedText, setSearchedText] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState(null);
  const [classDetails, setClassDetails] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const requestedClassIds = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    const timeout = setTimeout(() => setSearchedText(text), 350);
    return () => clearTimeout(timeout);
  }, [text]);
  useEffect(() => {
    [...items, ...(selected ? [selected] : [])].forEach(item => {
      if (item.entity.type !== 'classes' || item.entity.descriptor || requestedClassIds.current.has(item.entity.id)) return;
      requestedClassIds.current.add(item.entity.id);
      SchoolClassService.getSchoolClass(item.entity.id)
        .then(schoolClass => { if (mounted.current) setClassDetails(current => ({ ...current, [item.entity.id]: schoolClass })); })
        .catch(() => {});
    });
  }, [items, selected]);
  const actorOptions = [...new Map([...actorCatalog, ...items.map(item => item.actor), ...(selected ? [selected.actor] : [])]
    .filter(Boolean).map(actor => [actor.id, [actor.id, actor.label || actor.id]])).values()];
  actorIds.forEach(id => { if (!actorOptions.some(option => option[0] === id)) actorOptions.push([id, id]); });
  const teacherChoices = [...new Map([...teacherIds.map(id => [id, id]), ...items.flatMap(item => (item.references || [])
    .filter(ref => ref.type === 'teachers').map(ref => [ref.id, ref.id])), ...teacherOptions]
    .map(option => [option[0], option])).values()];
  const classTypeChoices = [...new Map([...classTypeIds.map(id => [id, id]), ...items.flatMap(item => (item.references || [])
    .filter(ref => ref.type === 'classTypes').map(ref => [ref.id, ref.id])), ...classTypeOptions]
    .map(option => [option[0], option])).values()];
  const invalidRange = isValidDateValue(from) && isValidDateValue(to) && from > to;
  const filters = { types, actions, actorIds, teacherIds, classTypeIds, entityId, text: searchedText, from: localStart(from), to: localEnd(to), limit: pageSize };
  const filterKey = JSON.stringify(filters);
  useEffect(() => { setEntityId(relatedRecordId); }, [relatedRecordId]);
  useEffect(() => {
    let active = true;
    Promise.allSettled([
      Promise.resolve().then(() => TeacherService.getTeachers()),
      Promise.resolve().then(() => SettingsService.getSettings()),
      Promise.resolve().then(() => (service.getActors ? service.getActors() : [])),
    ]).then(([teachers, settings, actors]) => {
      if (!active) return;
      if (teachers.status === 'fulfilled' && Array.isArray(teachers.value)) {
        setTeacherOptions(teachers.value.map(teacher => [teacher.id, [teacher.firstName, teacher.lastName].filter(Boolean).join(' ') || teacher.id]));
      }
      if (settings.status === 'fulfilled' && Array.isArray(settings.value)) {
        const lessonTypes = getLessonTypesSetting(settings.value);
        setClassTypeOptions(lessonTypes.types.map(type => [type.classTypeId, type.name || type.classTypeId]));
      }
      if (actors.status === 'fulfilled' && Array.isArray(actors.value)) setActorCatalog(actors.value);
    });
    return () => { active = false; };
  }, [service]);
  const load = async (nextCursor, revision, currentFilters) => {
    setBusy(true);
    setError('');
    try {
      const result = await service.listEvents({ ...currentFilters, cursor: nextCursor });
      if (generation.current !== revision) return;
      setItems(previous => (nextCursor ? [...previous, ...result.items] : result.items));
      setCursor(result.cursor);
    } catch (failure) {
      if (generation.current === revision) setError(failure.message || 'Nie udało się pobrać logu.');
    } finally {
      if (generation.current === revision) setBusy(false);
    }
  };
  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setCursor(null);
    if (invalidRange) { setError('Data „Od” nie może być późniejsza niż data „Do”.'); return undefined; }
    load(null, generation.current, filters);
    return () => { generation.current += 1; };
  }, [filterKey, invalidRange, service]);
  const openDetail = async item => {
    setError('');
    try { setSelected(await service.getEvent(item.eventId)); } catch (failure) { setError(failure.message || 'Nie udało się pobrać szczegółów.'); }
  };
  const addActor = () => {
    const id = actorInput.trim();
    if (id && !actorIds.includes(id)) setActorIds([...actorIds, id]);
    setActorInput('');
  };
  const selectedRoute = selected && detailRoute(selected.entity, location.search);
  const selectedDescription = selected && auditRecordDescription(selected, teacherOptions, classDetails[selected.entity.id]).label;
  return (
    <div className="audit-log-page mb-5">
      <div className="audit-log-heading">
        <div><h2 className="manage-title mb-1">Dziennik zmian</h2><p>Historia operacji w bieżącej szkole</p></div>
        <button type="button" className="btn manage-teal-button" onClick={() => history.goBack()}>Powrót</button>
      </div>
      <div className="audit-filters">
        <div className="audit-filter-grid">
          <MultiFilter label="Typ rekordu" options={RECORD_TYPES} value={types} onChange={setTypes} />
          <MultiFilter label="Operacja" options={ACTIONS} value={actions} onChange={setActions} />
          <MultiFilter label="Wykonawca" options={actorOptions} value={actorIds} onChange={setActorIds} />
          <MultiFilter label="Nauczyciel" options={teacherChoices} value={teacherIds} onChange={setTeacherIds} />
          <MultiFilter label="Typ lekcji" options={classTypeChoices} value={classTypeIds} onChange={setClassTypeIds} />
          <div className="input-group">
            <input
              className="form-control"
              aria-label="ID wykonawcy"
              placeholder="ID wykonawcy"
              value={actorInput}
              onChange={event => setActorInput(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') addActor(); }}
            />
            <div className="input-group-append"><button className="btn manage-teal-button" type="button" onClick={addActor}>Dodaj</button></div>
          </div>
          <input className="form-control" aria-label="Powiązany rekord" placeholder="ID ucznia, grupy lub innego rekordu" value={entityId} onChange={event => setEntityId(event.target.value)} />
          <input className="form-control" aria-label="Szukaj w logu" placeholder="Szukaj w logu…" value={text} onChange={event => setText(event.target.value)} />
          <PolishDateInput label="Od" name="audit-from" value={from} onChange={event => setFrom(event.target.value)} onBlur={() => {}} />
          <PolishDateInput label="Do" name="audit-to" value={to} onChange={event => setTo(event.target.value)} onBlur={() => {}} />
        </div>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => {
            setTypes([]); setActions([]); setActorIds([]); setTeacherIds([]); setClassTypeIds([]);
            setEntityId(''); setText(''); setFrom(''); setTo('');
          }}
        >Wyczyść filtry
        </button>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="audit-results">
        <div className="audit-results-heading">
          <div className="audit-results-title">Zdarzenia {busy && <span className="text-muted">· Ładowanie…</span>}</div>
          <label htmlFor="audit-page-size" className="mb-0">Pobierz po:
            <select id="audit-page-size" className="form-control d-inline-block ml-2" value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>
              <option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
            </select>
          </label>
        </div>
        <div className="table-responsive">
          <table className="table table-hover">
            <thead><tr><th>Data i godzina</th><th>Operacja</th><th>Rekord</th><th>Wykonawca</th><th>Zmiany</th></tr></thead>
            <tbody>{items.map(item => (
              <tr key={item.eventId}>
                <td>{displayDateTime(item.occurredAt)}</td>
                <td>{labelFor(ACTIONS, item.action)}</td>
                <td>
                  <button type="button" className="btn btn-link p-0 text-left" onClick={() => openDetail(item)}>
                    {auditRecordDescription(item, teacherOptions, classDetails[item.entity.id]).label}
                    <small className="d-block text-muted">
                      {labelFor(RECORD_TYPES, item.entity.type)}
                      {auditRecordDescription(item, teacherOptions, classDetails[item.entity.id]).current && ' · aktualny opis'}
                    </small>
                  </button>
                </td>
                <td>{item.actor.label}</td>
                <td>{item.changes.length} <button type="button" className="btn btn-link p-0 ml-2" onClick={() => openDetail(item)}>Szczegóły</button></td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        {!busy && !items.length && !error && (
          <p className="p-3">{cursor ? 'Na tej stronie nie ma dopasowań. Sprawdź kolejne zdarzenia.' : 'Brak zdarzeń dla wybranych filtrów.'}</p>
        )}
        {cursor && <button type="button" className="btn manage-teal-button m-3" disabled={busy} onClick={() => load(cursor, generation.current, filters)}>Pokaż kolejne</button>}
      </div>
      {selected && (
        <div className="audit-detail-backdrop">
          <aside className="audit-detail" role="dialog" aria-modal="true" aria-label="Szczegóły zdarzenia">
            <button className="btn btn-link float-right" type="button" onClick={() => setSelected(null)}>Zamknij</button>
            <h3>Szczegóły zdarzenia</h3>
            <p><strong>{labelFor(ACTIONS, selected.action)}</strong><br />{displayDateTime(selected.occurredAt)}</p>
            <p>Rekord: {selectedRoute ? (
              <Link to={selectedRoute} onClick={() => setSelected(null)}>{selectedDescription}</Link>
            ) : selectedDescription}
              <br />Wykonawca: {selected.actor.label}
              {selected.actor.role && <><br />{t('audit.actorRole')}: {selected.actor.role}</>}
              {selected.actor.contextKey && <><br />{t('audit.actorContext')}: {selected.actor.contextKey}</>}
            </p>
            <details><summary>Dane techniczne</summary>
              <p>ID rekordu: {selected.entity.id}<br />ID operacji: {selected.operationId}<br />ID zdarzenia: {selected.eventId}</p>
            </details>
            <h4>Zmiany</h4>
            <div className="table-responsive">
              <table className="table">
                <thead><tr><th>Pole</th><th>Przed</th><th>Po</th></tr></thead>
                <tbody>{selected.changes.map(change => (
                  <tr key={change.field}>
                    <td>{changeFieldLabel(change.field, selected.entity.studentLabels, t)}</td>
                    {change.before === '[ukryte]' && change.after === '[ukryte]' ? (
                      <td colSpan="2">Zmieniono (wartości ukryte)</td>
                    ) : (
                      <>
                        <td>{changeValueLabel(change.field, change.before, teacherOptions, classTypeOptions, t)}</td>
                        <td>{changeValueLabel(change.field, change.after, teacherOptions, classTypeOptions, t)}</td>
                      </>
                    )}
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

AuditLogPage.propTypes = { service: PropTypes.shape({ listEvents: PropTypes.func, getEvent: PropTypes.func }) };
AuditLogPage.defaultProps = { service: AuditService };
export default AuditLogPage;
